import { Routes, Route, Navigate } from "react-router-dom";
import AccountContainer from "../features/account";
import Dashboard from "../features/dashboard";
import Earnings from "../features/earnings";
import Social from "../features/social";
import Streaming from "../features/streaming";
import AuthorizeRoute from "@arelia-components/api-authorization/AuthorizeRoute";
import EmptyPage from "@arelia-components/empty-page";
import PerformanceRevenue from "../features/performance-revenue";
import { CustomersContainer, CustomerDetailsContainer } from "../features/hq/customers";
import { AdminRoute } from "./AdminRoute";
import ProfileContainer from "src/features/account/profile/ProfileContainer";
import { AuthMonitor } from "src/features/hq/auth-monitor";
import RecordingRoyaltiesDetails from "../features/earnings/streaming-royalties";
import HqDashboardContainer from "src/features/hq/dashboard/HqDashboardContainer";
import SubscriptionContainer from "src/features/account/subscription/SubscriptionContainer";
import { AppAccessRoute } from "./AppAccessRoute";
import ConnectionsContainer from "src/features/account/connections/ConnectionsContainer";
import PublishingRoyaltiesDetails from "src/features/earnings/publishing-royalties";
import MyVideosContainer from "src/features/social/my-videos/MyVideosContainer";
import PublishingRoyaltiesBreakdown from "src/features/earnings/publishing-royalties/details";
import MyContentContainer from "src/features/my-content";
import HqManageContainer from "src/features/hq/manage/HqManageContainer";
import CaptureFormContainer from "src/features/capture-form/CaptureFormContainer";
import Fans from "src/features/fans/Fans";
import FansListContainer from "src/features/fans/fans-list/FansListContainer";
import CampaignListContainer from "src/features/fans/campaigns/CampaignListContainer";
import CampaignDetailsContainer from "src/features/fans/campaigns/campaign-details/CampainDetailsContainer";
import SettingsContainer from "src/features/fans/settings/SettingsContainer";
import { TwilioMonitorContainer } from "src/features/hq/twilio-monitor/TwilioMonitorContainer";
import { TwilioRegistrationDetailsContainer } from "src/features/hq/twilio-monitor/twilio-reg-details/TwilioRegistrationDetailsContainer";
import SecurityContainer from "src/features/account/security/SecurityContainer";
import PostContainer from "src/features/social/post/PostContainer";

export const PATHS = {
  ACCOUNT: "/account",
  CONNECTIONS: "/account/connections",
  PROFILE: "/account/profile",
  SUBSCRIPTION: "/account/subscription",
  SECURITY: "/account/security",

  DASHBOARD: "/dashboard",
  SOCIAL: "/my-social",
  STREAMING: "/streaming",

  EARNINGS: "/earnings",
  PERFORMANCE_REVENUE: "/earnings/performance-revenue",
  STREAMING_ROYALTIES_DETAILS: "/earnings/streaming-royalties",
  PUBLISHING_ROYALTIES_DETAILS: "/earnings/publishing-royalties",

  MY_CONTENT: "/my-content",
  MY_CONTENT_SONGS: "/my-content/songs",

  FANS: "/my-fans",
  FANS_DASHBOARD: "/my-fans/dashboard",
  FANS_LIST: "/my-fans/list",
  CAMPAIGN_LIST: "/my-fans/campaigns/list",
  FANS_SETTINGS: "/my-fans/settings",

  CUSTOMERS: "/hq/customers",
  HQ_DASHBOARD: "/hq/dashboard",
  AUTH_MONITOR: "/hq/auth-app-monitor",
  TWILIO_MONITOR: "/hq/twilio-registration-monitor",
  MANAGE: "/hq/manage",

  EMPTY_PAGE: "/empty-page",

  STAY_CONNECTED: "/stay-connected",
} as const;

const routes = [
  {
    path: PATHS.PROFILE,
    element: <ProfileContainer />,
    requireAuth: true,
  },
  {
    path: PATHS.SUBSCRIPTION + "/*",
    element: <SubscriptionContainer />,
    requireAuth: true,
  },
  {
    path: PATHS.CONNECTIONS + "/*",
    element: (
      <AppAccessRoute>
        <ConnectionsContainer />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.SECURITY + "/*",
    element: (
      <AppAccessRoute>
        <SecurityContainer />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.DASHBOARD,
    element: (
      <AppAccessRoute>
        <Dashboard />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.EARNINGS + "/*",
    element: (
      <AppAccessRoute>
        <Earnings />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.PERFORMANCE_REVENUE + "/*",
    element: (
      <AppAccessRoute>
        <PerformanceRevenue />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.STREAMING_ROYALTIES_DETAILS + "/*",
    element: (
      <AppAccessRoute>
        <RecordingRoyaltiesDetails />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.PUBLISHING_ROYALTIES_DETAILS + "/",
    element: (
      <AppAccessRoute>
        <PublishingRoyaltiesDetails />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.PUBLISHING_ROYALTIES_DETAILS + "/details",
    element: (
      <AppAccessRoute>
        <PublishingRoyaltiesBreakdown />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.SOCIAL + "/*",
    element: (
      <AppAccessRoute>
        <Social />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.SOCIAL + "/my-videos",
    element: (
      <AppAccessRoute>
        <MyVideosContainer />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.SOCIAL + "/create-a-post",
    element: (
      <AppAccessRoute>
        <PostContainer />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.MY_CONTENT + "/*",
    element: (
      <AppAccessRoute>
        <MyContentContainer />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.FANS + "/*",
    element: (
      <AppAccessRoute>
        <Fans />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.FANS_LIST,
    element: (
      <AppAccessRoute>
        <FansListContainer />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.CAMPAIGN_LIST,
    element: (
      <AppAccessRoute>
        <CampaignListContainer />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.CAMPAIGN_LIST + "/:id",
    element: (
      <AppAccessRoute>
        <CampaignDetailsContainer />
      </AppAccessRoute>
    ),
  },
  {
    path: PATHS.FANS_SETTINGS,
    element: (
      <AppAccessRoute>
        <SettingsContainer />
      </AppAccessRoute>
    ),
  },
  {
    path: PATHS.STREAMING + "/*",
    element: (
      <AppAccessRoute>
        <Streaming />
      </AppAccessRoute>
    ),
    requireAuth: true,
  },
  {
    path: PATHS.ACCOUNT + "/*",
    element: <AccountContainer />,
    requireAuth: true,
  },
  {
    path: PATHS.EMPTY_PAGE + "/*",
    element: <EmptyPage />,
  },
  {
    path: PATHS.CUSTOMERS,
    element: (
      <AdminRoute>
        <CustomersContainer />
      </AdminRoute>
    ),
  },
  {
    path: PATHS.HQ_DASHBOARD,
    element: (
      <AdminRoute>
        <HqDashboardContainer />
      </AdminRoute>
    ),
  },
  {
    path: PATHS.CUSTOMERS + "/:id",
    element: (
      <AdminRoute>
        <CustomerDetailsContainer />
      </AdminRoute>
    ),
  },
  {
    path: PATHS.AUTH_MONITOR,
    element: (
      <AdminRoute>
        <AuthMonitor />
      </AdminRoute>
    ),
  },
  {
    path: PATHS.TWILIO_MONITOR,
    element: (
      <AdminRoute>
        <TwilioMonitorContainer />
      </AdminRoute>
    ),
  },
  {
    path: `${PATHS.TWILIO_MONITOR}/:id`,
    element: (
      <AdminRoute>
        <TwilioRegistrationDetailsContainer />
      </AdminRoute>
    )
  },
  {
    path: PATHS.MANAGE,
    element: (
      <AdminRoute>
        <HqManageContainer />
      </AdminRoute>
    ),
  },
  {
    path: PATHS.STAY_CONNECTED + "/:publicId",
    element: <CaptureFormContainer />,
    requireAuth: false,
  },
];

export default function AppRoutes() {
  return (
    <Routes>
      {routes.map((route, index) => {
        const { element, requireAuth, ...rest }: any = route;
        return (
          <Route
            key={index}
            {...rest}
            element={
              requireAuth ? (
                <AuthorizeRoute {...rest} element={element} />
              ) : (
                element
              )
            }
          />
        );
      })}
      <Route path="*" element={<Navigate to={PATHS.DASHBOARD} />} />
    </Routes>
  );
}
